/**
 * Modules and permissions (brief section 9).
 *
 * Every feature is a module that can be switched off with MODULES_ENABLED, and
 * every action is guarded by a `module.action[.scope]` permission. Each
 * permission belongs to exactly one module: when the module is off, the
 * permission is never granted. Roles (config/portal.json) are bundles of these
 * permissions; the server enforces them on every request.
 */

export const MODULES = [
  'capture',
  'people',
  'leads',
  'todo',
  'voice',
  'events',
  'recap',
  'kiosk',
  'travel',
  'content',
  'packages',
  'roi',
  'expenses',
  'admin',
  // Nov 3, 2026: meetings at shows, documented in the portal and sent to HubSpot.
  'meetings',
] as const;
export type ModuleId = (typeof MODULES)[number];

export const PERMISSIONS = {
  // Capture: scan, Add contact, Hand the tablet
  'contacts.create': 'capture',
  'contacts.scan': 'capture',
  'contacts.handoff': 'capture',
  // People: contacts list and sheet
  'contacts.read.own': 'people',
  'contacts.read.all': 'people',
  'contacts.update.own': 'people',
  'contacts.update.all': 'people',
  'contacts.delete.own': 'people',
  'contacts.delete.all': 'people',
  // Leads
  'leads.create': 'leads',
  'leads.read.own': 'leads',
  'leads.read.all': 'leads',
  'leads.update.own': 'leads',
  'leads.update.all': 'leads',
  'leads.qualify': 'leads',
  'leads.delete.own': 'leads',
  'leads.delete.all': 'leads',
  // To do and voice
  'todo.use': 'todo',
  'todo.voice': 'voice',
  'voice.qualify': 'voice',
  // Events, recap and wrap-up
  'events.read': 'events',
  'events.manage': 'events',
  'events.notes': 'events',
  'recap.view': 'recap',
  'events.wrapup': 'recap',
  // Booth kiosk
  'kiosk.start': 'kiosk',
  'kiosk.content.manage': 'kiosk',
  // Travel
  'travel.read': 'travel',
  'travel.items.add': 'travel',
  'travel.logistics.manage': 'travel',
  'travel.team.view': 'travel',
  // Content
  'content.read': 'content',
  'content.manage': 'content',
  // Packages and prospect pages
  'packages.create': 'packages',
  'packages.read.own': 'packages',
  'packages.read.all': 'packages',
  // ROI
  'roi.use': 'roi',
  'roi.restore': 'roi',
  'roi.update.all': 'roi',
  // Expenses
  'expenses.create': 'expenses',
  // Administration (admins only, no configuration setting)
  'admin.users.read': 'admin',
  'admin.users.manage': 'admin',
  'admin.audit.read': 'admin',
  'admin.privacy.erase': 'admin',
  'admin.packages.revoke': 'admin',
  'admin.kiosk.pin': 'admin',
  'admin.hubspot.manage': 'admin',
  // Round 2: Settings > Reset staging data, on staging only (never granted elsewhere).
  'admin.staging.reset': 'admin',
  // Meetings (Nov 3, 2026): the whole team sees every meeting.
  'meetings.create': 'meetings',
  'meetings.read.all': 'meetings',
  'meetings.update.own': 'meetings',
  'meetings.update.all': 'meetings',
  'meetings.delete.own': 'meetings',
} as const satisfies Record<string, ModuleId>;

export type Permission = keyof typeof PERMISSIONS;

export const PERMISSION_IDS = Object.keys(PERMISSIONS) as Permission[];

export function isPermission(value: string): value is Permission {
  return Object.hasOwn(PERMISSIONS, value);
}

/**
 * Expands a role's permission list. An entry is either an exact permission,
 * `*` (every permission) or a prefix wildcard such as `admin.*`.
 * Returns the unknown entries separately so configuration checks can refuse them.
 */
export function expandPermissions(entries: readonly string[]): {
  permissions: Set<Permission>;
  unknown: string[];
} {
  const permissions = new Set<Permission>();
  const unknown: string[] = [];
  for (const entry of entries) {
    if (entry === '*') {
      PERMISSION_IDS.forEach((p) => permissions.add(p));
    } else if (entry.endsWith('.*')) {
      const prefix = entry.slice(0, -1);
      const matches = PERMISSION_IDS.filter((p) => p.startsWith(prefix));
      if (matches.length === 0) unknown.push(entry);
      matches.forEach((p) => permissions.add(p));
    } else if (isPermission(entry)) {
      permissions.add(entry);
    } else {
      unknown.push(entry);
    }
  }
  return { permissions, unknown };
}

/**
 * Modules switched on by MODULES_ENABLED: a comma-separated list of module IDs.
 * Empty means every module. Unknown IDs are returned so start-up can refuse them.
 */
export function parseModulesEnabled(value: string | undefined): {
  modules: ModuleId[];
  unknown: string[];
} {
  const entries = (value ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
  if (entries.length === 0) return { modules: [...MODULES], unknown: [] };
  const known = MODULES as readonly string[];
  return {
    modules: MODULES.filter((m) => entries.includes(m)),
    unknown: entries.filter((entry) => !known.includes(entry)),
  };
}

/**
 * The permissions a user holds: the union of their roles' permissions, limited
 * to the modules switched on. Role keys missing from the configuration grant nothing.
 */
export function resolvePermissions(
  roleKeys: readonly string[],
  roles: Readonly<Record<string, { permissions: readonly string[] }>>,
  enabledModules: readonly ModuleId[],
): Permission[] {
  const granted = new Set<Permission>();
  for (const key of roleKeys) {
    const role = Object.hasOwn(roles, key) ? roles[key] : undefined;
    if (!role) continue;
    expandPermissions(role.permissions).permissions.forEach((p) => granted.add(p));
  }
  return PERMISSION_IDS.filter((p) => granted.has(p) && enabledModules.includes(PERMISSIONS[p]));
}

/**
 * Whether a user may perform an action on one record (brief section 9): the
 * `.all` scope allows it on any record, the `.own` scope only on the user's own
 * records, and an unscoped permission on any record.
 * Example: canActOn(permissions, 'contacts.update', isOwn).
 */
export function canActOn(permissions: Iterable<string>, action: string, isOwn: boolean): boolean {
  const held = new Set(permissions);
  return held.has(`${action}.all`) || held.has(action) || (isOwn && held.has(`${action}.own`));
}
